import React from 'react';
import { TextInput } from '@common/components/form/inputs/text';
import { PerformanceFormComponent } from '@modules/performance/types';
import { useTranslation } from 'react-i18next';
import { getRatingColor } from '@modules/performance/containers/performance-review/components';

type Props = {
  item: PerformanceFormComponent;
};

enum RatingType {
  POTENTIAL = 'potential',
  PERFORMANCE = 'performance',
  RETENTION_RISK = 'retention_risk',
  IMPACT_OF_LOSS = 'impact_of_loss',
}

const TalentReview = ({ item }: Props) => {
  const { t } = useTranslation();

  const getLabel = (value: number) => {
    switch (value) {
      case 1:
        return t('performance:low');
      case 2:
        return t('performance:moderate');
      case 3:
        return t('performance:high');
      default:
        return t('performance:moderate');
    }
  };
  // Get the settings from the item prop
  const settings = item?.settings || {};
  const retentionRisk = settings.retention_risk;
  const impactOfLoss = settings.impact_of_loss;

  const RatingComponent: React.FC<{
    title: string;
    description: string;
    value: number;
    type: RatingType;
  }> = ({ title, description, value, type }) => {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4 tw-py-4 tw-text-left">
        <div className="tw-text-base tw-font-semibold tw-text-gray-900">
          {title}
        </div>
        <div className="tw-text-sm tw-font-normal tw-text-gray-600">
          {description}
        </div>
        <div className="tw-flex tw-border tw-border-gray-200 tw-border-solid tw-rounded-lg tw-overflow-hidden">
          {[1, 2, 3].map((ratingValue, index) => {
            const isSelected = value === ratingValue;
            const colorClass = getRatingColor(type, ratingValue);

            return (
              <React.Fragment key={ratingValue}>
                <div
                  className={`tw-text-center tw-flex-1 tw-font-medium tw-p-2 tw-transition-colors ${
                    isSelected
                      ? `${colorClass} tw-bg-opacity-10 tw-text-white`
                      : 'tw-text-gray-textGray hover:tw-bg-gray-50'
                  }`}
                >
                  {ratingValue}
                </div>
                {index < 2 && (
                  <div className="tw-w-px tw-border tw-border-solid tw-border-gray-light-50" />
                )}
              </React.Fragment>
            );
          })}
        </div>
        <div className="tw-flex tw-justify-center">
          <div
            className="tw-px-4 tw-py-2 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-font-medium"
          >
            {getLabel(value)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <RatingComponent
        title={t('performance:rate_performance_of_employee')}
        description={t('performance:rate_employee_performance_description')}
        value={2}
        type={RatingType.PERFORMANCE}
      />

      <RatingComponent
        title={t('performance:rate_potential_of_employee')}
        description={t('performance:rate_potential_description')}
        value={1}
        type={RatingType.POTENTIAL}
      />

      {retentionRisk && (
        <RatingComponent
          title={t('performance:rate_retention_risk_of_employee')}
          description={t('performance:rate_retention_risk_description')}
          value={1}
          type={RatingType.RETENTION_RISK}
        />
      )}

      {impactOfLoss && (
        <div className="tw-flex tw-flex-col tw-gap-4 tw-p-4 tw-text-left">
          <div className="tw-text-base tw-font-semibold tw-text-gray-900">
            {t('performance:enter_impact_of_loss_for_employee')}
          </div>
          <div className="tw-text-sm tw-font-normal tw-text-gray-600">
            {t('performance:loss_for_employee_description')}
          </div>
          <TextInput
            placeholder={t(
              'learning:component_type_question_text_placeholder'
            )}
            className="tw-mt-2"
          />
        </div>
      )}
    </>
  );
};

export default TalentReview;
